import { useState, useRef, useEffect, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import type { PlayerRef } from '@remotion/player';
import type { AppConfig, Segment } from '@/lib/types';
import { parseDuration, getEffectiveDuration } from '@/lib/utils';

export function useTimeline(config: AppConfig, onChange: (config: AppConfig) => void) {
  const musicContainerRef = useRef<HTMLDivElement>(null);
  const voContainerRef = useRef<HTMLDivElement>(null);
  const musicInstanceRef = useRef<WaveSurfer | null>(null);
  const voInstanceRef = useRef<WaveSurfer | null>(null);
  const playerRef = useRef<PlayerRef>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const isSyncing = useRef(false);

  const syncSegments = (segments: Segment[]): Segment[] => {
    const sorted = [...segments].sort((a, b) => a.startTime - b.startTime);
    let lastTime = 0;
    return sorted.map(s => {
      const updated = { ...s, startTime: lastTime };
      lastTime = s.endTime;
      return updated;
    });
  };

  const updateSegment = (id: string, updates: Partial<Segment>) => {
    const newSegments = config.segments.map(s => s.id === id ? { ...s, ...updates } : s);
    onChange({
      ...config,
      segments: syncSegments(newSegments),
    });
  };

  const deleteSegment = (id: string) => {
    const newSegments = config.segments.filter(s => s.id !== id);
    onChange({
      ...config,
      segments: syncSegments(newSegments),
    });
  };

  const addSegmentWithLines = (text: string, endTime: number) => {
    // Find if this text sequence is already partially or fully in segments
    // For production, we match by exact text content to find the "slot"
    const existingIdx = config.segments.findIndex(s => s.text.trim() === text.trim());
    
    let newSegments = [...config.segments];
    
    if (existingIdx !== -1) {
       // Update existing marker
       newSegments[existingIdx] = { ...newSegments[existingIdx], endTime } as Segment;
    } else {
       // Create new marker. The startTime will be handled by syncSegments.
       const newSegment: Segment = {
         id: Math.random().toString(36).substring(2, 11),
         text: text,
         startTime: 0, // Will be calculated
         endTime,
         transition: 'fade',
       };
       newSegments.push(newSegment);
    }

    onChange({
      ...config,
      segments: syncSegments(newSegments),
    });
  };

  const togglePlay = useCallback(() => {
    if (!playerRef.current) return;
    
    if (playerRef.current.isPlaying()) {
      playerRef.current.pause();
    } else {
      playerRef.current.play();
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    if (!playerRef.current) return;
    const fps = 30;
    playerRef.current.seekTo(Math.floor(time * fps));
    
    // Immediate visual feedback for waveforms
    musicInstanceRef.current?.setTime(time);
    voInstanceRef.current?.setTime(time);
    setCurrentTime(time);
  }, []);

  const onFrameUpdate = useCallback((frame: number) => {
    const fps = 30;
    const time = frame / fps;
    setCurrentTime(time);
  }, []);

  return {
    musicContainerRef,
    voContainerRef,
    musicInstanceRef,
    voInstanceRef,
    playerRef,
    isPlaying,
    setIsPlaying,
    currentTime,
    setCurrentTime,
    duration: getEffectiveDuration(config),
    isSyncing,
    updateSegment,
    deleteSegment,
    addSegmentWithLines,
    togglePlay,
    seekTo,
    onFrameUpdate,
  };
}
